import { ChevronLeft, ChevronRight } from 'lucide-react';
import { STRINGS } from '@/strings/strings';

/**
 * v3.1 §5 Bausteine — Große Ansicht primitive.
 *
 * Square by construction; sizes to the lane's inner width (680 from
 * tablet up).
 *
 * ═══ 2026-08-23 OVERLAY REMOVAL — NO TITLE ON THE IMAGE ANYWHERE ═════
 *
 * Sebastian ruling 2026-08-23 after the anchor-review screen started
 * painting `envelope.title` across the anchor image (the v1.5 title-
 * emission commit woke a dormant overlay branch). The cover title
 * belongs in the composed cover image, set by the composer at I5, and
 * nowhere else. This primitive is now a pure `<img>` for both cover
 * and page slots. The panelBox math, the overlay branch, and the
 * DEFAULT_PANEL_BOX helpers have been deleted; there is no dormant
 * layer for a future title change to wake up again.
 *
 * Consequence a next reader must not "restore": the customer types a
 * title in the preview screen's edit field and does NOT see it appear
 * on the image. That is CORRECT until the cover render (I5) exists;
 * the composed cover image will carry the title in the pixels.
 *
 * ═══ ARROWS ══════════════════════════════════════════════════════════
 *
 * v3.1 §5 line 219 verbatim: "h-11 w-11 rounded-full bg-dream-white/90,
 * im Bild links und rechts, vertikal zentriert." 44 px = T8 touch
 * target.
 */

type SlotKind =
  | {
      kind: 'cover';
      imageUrl: string | null;
      imageAlt: string;
      /**
       * Cover-regen slice (Sebastian ruling 2026-09-05) — optional
       * running-state props, symmetric with the page variant below.
       * When 'rendering' + runningLabel present, StageView paints the
       * same scrim over the cover image that it paints over pages
       * mid-regen. Envelope-driven read on every render — nav-away +
       * back re-derives from the same envelope prop. Absent on the
       * pre-Freigabe preview screen (no cover regens before customer
       * review).
       */
      renderState?: 'rendering' | 'ready' | 'retry_available' | 'blocked';
      runningLabel?: string;
      /**
       * Sebastian ruling 2026-09-08 — screen-local copy for the
       * retry-scrim (retry_available state). Same shape as
       * `runningLabel` above: caller supplies the string so this
       * primitive stays state-agnostic. When present + state is
       * `retry_available`, a static (no spinner) scrim renders with
       * this copy over the image. PreviewScreen passes
       * `preview.tileFailed`; ReviewScreen passes `review.tileFailed`
       * (post-payment support-address wording).
       */
      retryLabel?: string;
    }
  | {
      kind: 'page';
      /**
       * Sebastian ruling 2026-09-01 — RAW picked scene image (no
       * text panel). Preview large view shows this by preference so
       * the customer sees the illustration untouched, matching the
       * four preview tiles. `composedImageUrl` stays as the
       * fallback for the case where the raw sign failed but the
       * composed one is fine, and remains the sole source on the
       * post-payment `customer_review` full-book view (a different
       * screen, `ReviewScreen.tsx`, which deliberately keeps the
       * panelled image).
       */
      rawImageUrl: string | null;
      composedImageUrl: string | null;
      imageAlt: string;
      /**
       * Position-1 Commit-4 revisit (Sebastian ruling 2026-09-04 late-
       * evening). Optional: when 'rendering', paint the running-state
       * scrim over the image with the regenRunningLabel. Reads on every
       * render — no local state, no snapshot at click time — so the
       * customer sees the scrim continuously while any regen is in
       * flight, whether the dialog is open or closed, whether he
       * navigated away with the arrows and back. Undefined on the
       * pre-Freigabe preview screen (no customer regens there).
       */
      renderState?: 'rendering' | 'ready' | 'retry_available' | 'blocked';
      /**
       * Position-1 Commit-4 revisit — the label to render inside the
       * scrim. Passed by the caller so this primitive stays free of
       * feature-specific strings.
       */
      runningLabel?: string;
      /**
       * Sebastian ruling 2026-09-08 — screen-local copy for the
       * retry-scrim on the page variant. Symmetric with the cover
       * variant's `retryLabel` above. Caller supplies the string.
       */
      retryLabel?: string;
    };

export interface StageViewProps {
  readonly slot: SlotKind;
  /** True when there is a previous slot in the deck. */
  readonly canGoPrev: boolean;
  /** True when there is a next slot in the deck. */
  readonly canGoNext: boolean;
  readonly onPrev: () => void;
  readonly onNext: () => void;
  /**
   * Sebastian ruling 2026-09-24 — small on-image pill in the top-
   * right corner naming the customer's position in the deck
   * ("1 von 5" style). Added after iPhone testing showed customers
   * who did not scroll past the fold to the tile row assumed the
   * big image was the only image. Caller supplies the string
   * ready-rendered (via `STRINGS.book.preview.positionCounter`)
   * so this primitive stays feature-agnostic. Omitting the prop
   * hides the pill.
   */
  readonly counter?: string;
}

export function StageView({ slot, canGoPrev, canGoNext, onPrev, onNext, counter }: StageViewProps) {
  const imageUrl =
    slot.kind === 'cover' ? slot.imageUrl : (slot.rawImageUrl ?? slot.composedImageUrl);

  // Running-state scrim over the big image while the slot is mid-
  // render. Both slot kinds carry the same envelope-driven pair
  // (renderState + runningLabel) as of the cover-regen slice; the
  // scrim gate is symmetric so nav-away + back re-derives from the
  // same envelope prop for cover and page alike.
  const showRunningScrim =
    slot.renderState === 'rendering' &&
    typeof slot.runningLabel === 'string' &&
    slot.runningLabel.length > 0;
  // Retry-scrim (Sebastian ruling 2026-09-08) — static overlay when
  // the slot is in `retry_available` and the caller has supplied a
  // retryLabel. Static, no spinner ("nothing is happening, a spinner
  // would lie" per the tileFailed comment in strings.ts). PreviewScreen
  // supplies pre-payment copy; ReviewScreen supplies post-payment
  // copy with the support address.
  const showRetryScrim =
    slot.renderState === 'retry_available' &&
    typeof slot.retryLabel === 'string' &&
    slot.retryLabel.length > 0;

  return (
    <div
      className="@container aspect-square w-full rounded-xl shadow-xl overflow-hidden relative bg-dream-white"
      aria-label={STRINGS.book.preview.bigViewAriaLabel}
      role="group"
    >
      {imageUrl && (
        <img
          src={imageUrl}
          alt={slot.imageAlt}
          className="h-full w-full object-cover"
          // Sebastian ruling 2026-09-24 — the stage image is the
          // primary picture on the screen (customer_review,
          // read-only PostOrderView, PreviewScreen). High priority
          // tells Safari + Chromium to fetch it before the 25
          // thumb-strip images that share the same composed-URL
          // JPEG (~150-300 KB each). Async decoding avoids
          // blocking paint on the composited layer.
          fetchPriority="high"
          decoding="async"
        />
      )}

      {showRunningScrim && (
        <div
          aria-live="polite"
          className="absolute inset-0 flex items-center justify-center bg-dream-navy/40 backdrop-blur-[1px]"
        >
          <span className="rounded-lg bg-dream-white/95 px-4 py-2 text-sm font-semibold text-dream-navy shadow-sm text-center max-w-[80%]">
            {(slot as { runningLabel: string }).runningLabel}
          </span>
        </div>
      )}

      {showRetryScrim && (
        <div
          role="status"
          aria-live="polite"
          className="absolute inset-0 flex items-center justify-center bg-dream-navy/40 backdrop-blur-[1px]"
        >
          <span className="rounded-lg bg-dream-white/95 px-4 py-3 text-sm text-dream-navy shadow-sm text-center max-w-[85%]">
            {(slot as { retryLabel: string }).retryLabel}
          </span>
        </div>
      )}

      {counter !== undefined && counter.length > 0 && (
        <span
          aria-hidden
          className="absolute right-3 top-3 rounded-full bg-dream-navy/70 px-2.5 py-1 text-xs font-semibold text-dream-white shadow-sm"
        >
          {counter}
        </span>
      )}

      {canGoPrev && (
        <button
          type="button"
          onClick={onPrev}
          aria-label={STRINGS.book.preview.navPrev}
          className="absolute left-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-dream-white/90 flex items-center justify-center text-dream-navy shadow-sm transition-colors hover:bg-dream-white focus-visible:outline-2 focus-visible:outline-dream-navy/50"
        >
          <ChevronLeft aria-hidden className="h-5 w-5" />
        </button>
      )}

      {canGoNext && (
        <button
          type="button"
          onClick={onNext}
          aria-label={STRINGS.book.preview.navNext}
          className="absolute right-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-dream-white/90 flex items-center justify-center text-dream-navy shadow-sm transition-colors hover:bg-dream-white focus-visible:outline-2 focus-visible:outline-dream-navy/50"
        >
          <ChevronRight aria-hidden className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
